import React from "react";
import { TopBar } from "@/components/TopBar";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plus, Save, X, RefreshCw, Power } from "lucide-react";

const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60 disabled:opacity-60";
const labelCls = "text-[13px] font-semibold text-slate-200";
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

/**
 * Admin CRUD page for a simple master (bahan, supplier): form + table,
 * deactivate instead of delete. fields: [{key, label, type?, required?, immutable?, format?, placeholder?}]
 */
export default function SimpleCrudPage({ testId, title, subtitle, fields, api, rowKey = (r) => r.id, lihatSaja = false }) {  // lihatSaja: Manager (2026-10-12)
  const empty = React.useMemo(
    () => Object.fromEntries(fields.map((f) => [f.key, ""])),
    [fields],
  );
  const [rows, setRows] = React.useState([]);
  const [form, setForm] = React.useState(empty);
  const [editingId, setEditingId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setRows(await api.list()); } catch (e) { setError(errText(e)); } finally { setLoading(false); }
  }, [api]);
  React.useEffect(() => { load(); }, [load]);

  const flash = (m) => { setNotice(m); setError(null); setTimeout(() => setNotice(null), 2500); };
  const reset = () => { setForm(empty); setEditingId(null); };

  const toPayload = () => Object.fromEntries(
    fields
      .filter((f) => !(editingId && f.immutable))
      .map((f) => {
        const v = form[f.key];
        if (f.type === "number") return [f.key, v === "" ? null : Number(v)];
        return [f.key, v === "" && !f.required ? null : v];
      }),
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) { await api.update(editingId, toPayload()); flash("Data diperbarui"); }
      else { await api.create(toPayload()); flash("Data ditambahkan"); }
      reset();
      await load();
    } catch (e2) { setError(errText(e2)); } finally { setSaving(false); }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm(Object.fromEntries(fields.map((f) => [f.key, r[f.key] ?? ""])));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const toggle = async (r) => {
    try { await api.update(r.id, { is_active: !r.is_active }); await load(); } catch (e) { setError(errText(e)); }
  };

  return (
    <div data-testid={`${testId}-page`} className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">{title}</h1>
          <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
        </div>
        {notice && <div data-testid={`${testId}-notice`} className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">{notice}</div>}
        {error && <div data-testid={`${testId}-error`} className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}

        {!lihatSaja && <form data-testid={`${testId}-form`} onSubmit={onSubmit} className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className={labelCls}>{f.label}</label>
              <input data-testid={`${testId}-${f.key}`} type={f.type === "number" ? "number" : "text"} min={f.type === "number" ? 0 : undefined}
                value={form[f.key]} required={f.required} disabled={!!editingId && f.immutable} placeholder={editingId ? undefined : f.placeholder}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} />
            </div>
          ))}
          <div className="flex items-end lg:col-span-4">
            <div className="ml-auto flex items-center gap-2">
              {editingId && <button type="button" data-testid={`${testId}-cancel`} onClick={reset} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:text-slate-100"><X size={15} /> Batal</button>}
              <button data-testid={`${testId}-submit`} type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
                {saving ? <Loader2 className="animate-spin" size={16} /> : editingId ? <Save size={16} /> : <Plus size={16} />}
                {editingId ? "Simpan" : "Tambah"}
              </button>
            </div>
          </div>
        </form>}

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
            <h2 className="text-[15px] font-bold text-soq-biru-tua">Daftar</h2>
            <button data-testid={`${testId}-refresh`} onClick={load} className="inline-flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-slate-200"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
          </div>
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" size={18} /> Memuat…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`${testId}-table`}>
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 bg-slate-900 border-b border-slate-800">
                    {fields.map((f) => <th key={f.key} className="px-4 py-3">{f.label}</th>)}
                    <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`${testId}-row-${rowKey(r)}`} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      {fields.map((f) => <td key={f.key} className="px-4 py-3 text-slate-300">{f.format ? f.format(r[f.key]) : (r[f.key] ?? "—")}</td>)}
                      <td className="px-4 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 rounded border ${r.is_active ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-slate-600 bg-slate-700/30 text-slate-400"}`}>{r.is_active ? "Aktif" : "Nonaktif"}</span></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap space-x-1">
                        {!lihatSaja && <>
                        <button data-testid={`${testId}-edit-${rowKey(r)}`} onClick={() => startEdit(r)} className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300">Edit</button>
                        <button data-testid={`${testId}-toggle-${rowKey(r)}`} onClick={() => toggle(r)} title="Aktif/nonaktif" className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-red-500/40 hover:text-red-300"><Power size={13} /></button>
                        </>}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={fields.length + 2} className="px-4 py-8 text-center text-slate-500">Belum ada data.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
